import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "./api";
import { getAccessToken, getRefreshToken, setTokens, clearTokens, onAuthExpired } from "./token-store";
import { withProfileExtras, setProfileExtras } from "./local-profile";
import { writeExtensionAuth, clearExtensionAuth } from "./extension-bridge";
import { initFCM, removeFCMToken, onForegroundMessage } from "./fcm";
import { toast } from "sonner";

const USER_CACHE_KEY = "easylister.user.v2";

const AuthContext = createContext(null);

const readCachedUser = () => {
  try {
    const raw = localStorage.getItem(USER_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeCachedUser = (user) => {
  try {
    if (!user) localStorage.removeItem(USER_CACHE_KEY);
    else localStorage.setItem(USER_CACHE_KEY, JSON.stringify(user));
  } catch {
    // ignore
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [hydrated, setHydrated] = useState(false);

  const applySession = useCallback(({ user: rawUser, accessToken, refreshToken }) => {
    setTokens({ accessToken, refreshToken });
    const next = withProfileExtras(rawUser);
    writeCachedUser(next);
    writeExtensionAuth(next);
    setUser(next);
    // Request notification permission and register FCM token after login
    initFCM().catch(() => {});
    return next;
  }, []);

  const clearSession = useCallback(() => {
    clearTokens();
    writeCachedUser(null);
    clearExtensionAuth();
    setUser(null);
  }, []);

  useEffect(() => onAuthExpired(clearSession), [clearSession]);

  useEffect(() => {
    const bootstrap = async () => {
      const cached = readCachedUser();
      if (cached) setUser(cached);

      if (getAccessToken() || getRefreshToken()) {
        try {
          const { data } = await api.get("/protected/profile");
          const next = withProfileExtras(data.user);
          writeCachedUser(next);
          writeExtensionAuth(next);
          setUser(next);
          // Already logged in — ensure FCM token is registered
          initFCM().catch(() => {});
        } catch {
          clearSession();
        }
      }
      setHydrated(true);
    };
    bootstrap();

    // Show foreground push notifications as toasts
    onForegroundMessage((payload) => {
      const { title, body } = payload.notification || {};
      toast(title || "EasyLister", { description: body });
    });
  }, [clearSession]);

  const login = useCallback(
    async ({ email, password }) => {
      const { data } = await api.post("/auth/login", { email, password });
      return applySession(data);
    },
    [applySession],
  );

  const signup = useCallback(
    async ({ name, email, password, sellerName }) => {
      const { data } = await api.post("/auth/signup", { name, email, password, sellerName });
      if (sellerName) setProfileExtras(data.user.id, { sellerName });
      return applySession(data);
    },
    [applySession],
  );

  const googleAuth = useCallback(
    async ({ accessToken }) => {
      const { data } = await api.post("/auth/google", { accessToken });
      return applySession(data);
    },
    [applySession],
  );

  const forgotPassword = useCallback(async (email) => {
    const { data } = await api.post("/auth/forgot-password", { email });
    return data;
  }, []);

  const resetPassword = useCallback(
    async ({ token, password }) => {
      const { data } = await api.post("/auth/reset-password", { token, password });
      return applySession(data);
    },
    [applySession],
  );

  const logout = useCallback(async () => {
    const refreshToken = getRefreshToken();
    try {
      await removeFCMToken();
      if (refreshToken) await api.post("/auth/logout", { refreshToken });
    } catch {
      // best-effort — clear the local session regardless
    } finally {
      clearSession();
    }
  }, [clearSession]);

  const updateUser = useCallback(
    async ({ sellerName, ...patch }) => {
      if (!user) return null;
      let next = user;
      if (Object.keys(patch).length > 0 || sellerName !== undefined) {
        const body = { userId: user.id, ...patch };
        if (sellerName !== undefined) body.sellerName = sellerName;
        const { data } = await api.put("/auth/update-user", body);
        next = withProfileExtras(data.user);
      }
      if (sellerName !== undefined) {
        setProfileExtras(next.id, { sellerName });
        next = { ...next, sellerName };
      }
      writeCachedUser(next);
      writeExtensionAuth(next);
      setUser(next);
      return next;
    },
    [user],
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        hydrated,
        login,
        signup,
        googleAuth,
        logout,
        updateUser,
        forgotPassword,
        resetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
